import React, { useEffect, useState, useContext } from 'react';
import { AddressDetails, CardPayment, Totals } from '../common/CommonTypes';
import AddressManager from '../common/Address/AddressManager';
import {
  Journey,
  PageAbsolutePath,
  PageRelativePath,
  ProductChannel,
  RegistrationState,
  SagePayAccount,
  VehicleIdType
} from '../../consts/Common';
import { useNavigate } from 'react-router';
import Opayo from '../common/SinglePayment/Opayo';
import {
  calculateTotals,
  getSelectedProducts,
  hasPaymentSelected
} from '../common/ProductMethods';
import PaymentSummary from './PaymentSummary';
import { PaymentMethod } from '../common/Constants';
import { SiteContext } from '../../context/SiteContext';
import Checkbox from '../common/Checkbox/Checkbox';
import PageHeader from '../common/PageHeader';
import { ButtonContinue } from '../common/Button/ButtonContinue';
import { ButtonBack } from '../common/Button/ButtonBack';
import { useJourney } from './Layout';
import WorldPay from '../common/SinglePayment/WorldPay';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

export type LocationVehicleId = {
  idValue: string;
  vehicleIdType: VehicleIdType;
};

const SinglePayment = (): JSX.Element => {
  const [validateAddress, setValidateAddress] = useState(false);
  const [updateAddress, setUpdateAddress] = useState(false);
  const [redirect, setRedirect] = useState('');
  const [submitPayment, setSubmitPayment] = useState(false);
  const [paymentReady, setPaymentReady] = useState(false);
  const [voidTransaction, setVoidTransaction] = useState(false);
  const [captureTransaction, setCaptureTransaction] = useState(false);
  const [agreedSinglePaymentAgreement, setAgreedSinglePaymentAgreement] =
    useState(false);

  const { site } = useContext(SiteContext);
  const { journeyType } = site;
  const cms = site.content.singlePaymentPage;
  const useWorldPay = site.brand.worldPay;
  const isCallCentreUser =
    site.journeyType === Journey.CallCentre ||
    (site.journeyType === Journey.Customer && !!site.user?.userId);
  const isRenewal =
    site.journeyType === Journey.Renewals ||
    site.journeyType === Journey.CallCentre;

  const {
    journey,
    setAddressSinglePayment,
    onPaymentVoided,
    onPaymentFailure,
    onRegister,
    onPageChange
  } = useJourney();

  const {
    singlePaymentDetails,
    personalDetail,
    products,
    registrationState,
    orderNumbers
  } = journey;

  const { t } = useTranslation();

  const token = journey.token || site.user.token;

  const { billingAddress } = singlePaymentDetails;

  const navigate = useNavigate();

  const hasSubscription = hasPaymentSelected(
    products,
    PaymentMethod.Subscription
  );

  const hasDirectDebit = hasPaymentSelected(products, PaymentMethod.Monthly);

  const hasSinglePayment = hasPaymentSelected(products, PaymentMethod.Single);

  const { state } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (useWorldPay) setPaymentReady(true);
    console.log(`Call centre: ${isCallCentreUser}`);
  }, []);

  useEffect(() => {
    if (redirect !== '' && updateAddress === false) {
      navigate(redirect);
    }
  }, [redirect, updateAddress, validateAddress]);

  useEffect(() => {
    if (registrationState === RegistrationState.Success) {
      if (useWorldPay === true) {
        //need policy number
        setCaptureTransaction(true);
      } else {
        setRedirect(PageRelativePath.Confirmation);
        onPageChange(PageAbsolutePath.SinglePayment, true);
      }
    }

    if (registrationState === RegistrationState.VoidingPayment) {
      setVoidTransaction(true);
    }
  }, [registrationState]);

  const onPaymentCaptured = () => {
    setRedirect(PageRelativePath.Confirmation);
    onPageChange(PageAbsolutePath.SinglePayment, true);
  };

  const onPayClick = () => {
    setUpdateAddress(true);
    setValidateAddress(true);
  };

  const onAgreedSinglePaymentAgreementChange = () => {
    setAgreedSinglePaymentAgreement(!agreedSinglePaymentAgreement);
  };

  const onBack = () => {
    if (useWorldPay === false) {
      setUpdateAddress(true);
    }
    onPageChange(PageAbsolutePath.Summary, false);
    if (hasSubscription || hasDirectDebit) {
      if (journeyType === Journey.Customer) {
        navigate(PageRelativePath.DirectDebit, {
          state: {
            vehicleIdType: (state as LocationVehicleId).vehicleIdType
          }
        });
      } else {
        setRedirect(PageRelativePath.DirectDebit);
      }
    } else {
      if (journeyType === Journey.Customer) {
        navigate(PageRelativePath.Summary, {
          state: {
            vehicleIdType: (state as LocationVehicleId).vehicleIdType
          }
        });
      } else {
        setRedirect(PageRelativePath.Summary);
      }
    }
  };

  const onAddressUpdate = (address: AddressDetails) => {
    setAddressSinglePayment(address);
    setUpdateAddress(false);
  };

  const onAddressValidated = (isAddressValid: boolean) => {
    if (isAddressValid) {
      setValidateAddress(false);
      setSubmitPayment(true);
    } else {
      setValidateAddress(false);
    }
  };

  const onInvalidPaymentForm = () => {
    setSubmitPayment(false);
  };

  const enablePay =
    paymentReady &&
    !submitPayment &&
    ((agreedSinglePaymentAgreement && hasSubscription) || !hasSubscription);

  const selectedProducts = getSelectedProducts(products);

  const cardPaymentDescription = () => {
    if (hasSubscription && hasSinglePayment) {
      return 'Products';
    }

    if (hasSubscription) {
      return 'Subscription Products';
    }

    return 'Single pay products';
  };

  const totals: Totals = calculateTotals(selectedProducts, 1);
  const payment: CardPayment = {
    amount: totals.selectedSubscriptionPrice + totals.selectedSinglePrice,
    description: cardPaymentDescription(),
    surname: personalDetail.surname,
    firstName: personalDetail.firstName,
    address1: billingAddress.addressLine1 ?? '',
    address2: billingAddress.addressLine2 ?? '',
    city: billingAddress.townOrCity ?? '',
    postCode: billingAddress.postCode ?? '',
    country: billingAddress.country ?? '',
    state: billingAddress.region ?? '',
    phone: personalDetail.phone,
    email: personalDetail.email
  };

  const getInfoText = () => {
    if (hasSubscription && cms.subscriptionInformation != '') {
      return cms.subscriptionInformation;
    } else {
      return cms.information;
    }
  };

  return (
    <div>
      {submitPayment !== true && (
        <PageHeader
          title={cms.title}
          description={cms.description}
          information={getInfoText()}
          warning={cms.warning}
          descriptionClass={isRenewal ? 'p2' : ''}
        />
      )}
      <div data-testid='container-form' className='form-left-border'>
        {useWorldPay === true ? (
          <WorldPay
            payment={payment}
            token={token ?? site.user.token}
            isCallCentreUser={isCallCentreUser}
            registeringPolicy={
              registrationState === RegistrationState.Registering
            }
            voidTransaction={voidTransaction}
            captureTransaction={captureTransaction}
            policyNumbers={orderNumbers?.join(',') ?? ''}
            onPaymentSuccess={onRegister}
            onPaymentVoided={onPaymentVoided}
            onPaymentCaptured={onPaymentCaptured}
            countryCode={site.countryCode}
          />
        ) : (
          <Opayo
            token={token ?? site.user.token}
            account={
              isCallCentreUser
                ? SagePayAccount.CallCentre
                : SagePayAccount.Default
            }
            sagePayBaseUrl={site.sagePayBaseUrl}
            submitPayment={submitPayment}
            payment={payment}
            voidTransaction={voidTransaction}
            countryCode={site.countryCode}
            onError={() => setPaymentReady(false)}
            onLoaded={() => setPaymentReady(true)}
            onPaymentSuccess={onRegister}
            onPaymentFailure={onPaymentFailure}
            onPaymentVoided={onPaymentVoided}
            onInvalidPaymentForm={onInvalidPaymentForm}
            cardHeaderText={t(
              'customer-single-payment.enter-card-payment-details'
            )}
          />
        )}

        {submitPayment !== true && (
          <>
            {useWorldPay === false && (
              <div className='control m2-bottom'>
                <AddressManager
                  findAddressTitle={t(
                    'customer-single-payment.billing-address'
                  )}
                  initialAddress={billingAddress}
                  update={updateAddress}
                  validate={validateAddress}
                  token={token ?? site.user.token}
                  findAddressLabel={
                    isRenewal
                      ? t('customer-single-payment.different-billing-address')
                      : ''
                  }
                  onAddressUpdate={onAddressUpdate}
                  onAddressValidated={onAddressValidated}
                  allowManualEntry
                />
              </div>
            )}
            {hasSubscription && (
              <Checkbox
                id='single-payment-agreement'
                checked={agreedSinglePaymentAgreement}
                text={t('customer-single-payment.single-payment-agreement')}
                onChange={onAgreedSinglePaymentAgreementChange}
              />
            )}
            <PaymentSummary
              products={products.filter(
                (p) =>
                  p.selectedPaymentMethod === PaymentMethod.Single ||
                  p.selectedPaymentMethod === PaymentMethod.Subscription
              )}
              hasSubscription={hasSubscription}
              hasMonthly={hasDirectDebit}
              displayCustomerFriendlyName={isRenewal}
              hideInstallmentLabel={true}
              subscriptionTotalLabel={t(
                'customer-single-payment.subscription-total'
              )}
              oneOffPaymentLabel={t('customer-single-payment.one-off-payment')}
              productChannel={
                isRenewal
                  ? ProductChannel.RenewalJourney
                  : ProductChannel.DealerJourney
              }
              culture={site.cultureCode}
            />
          </>
        )}
      </div>

      {submitPayment !== true && (
        <div
          className={`flex-row form-nav ${
            isRenewal ? '' : 'mobile-width-90pc'
          }`}>
          {(useWorldPay === false ||
            registrationState === RegistrationState.Initial) && (
            <ButtonBack
              name='btnBack'
              onClick={onBack}
              text={t('customer-button.back-buttont-text')}
            />
          )}
          {useWorldPay === false && (
            <ButtonContinue
              name='btn-pay'
              text={t('customer-button.button-pay-now')}
              data-testid='pay-button'
              onClick={onPayClick}
              disabled={!enablePay}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default SinglePayment;
